import { useEffect, useId, useRef, useState } from 'react';
// THE PURE ENTRY (#1145, AUD-025). `@stripe/stripe-js` injects
// `js.stripe.com/v3` the moment the module is IMPORTED, and this file is in the
// main bundle — so Stripe's script ran, and set its cookies (`__stripe_mid`,
// `__stripe_sid`, `m`), on every page, the anonymous sign-in screens included.
// `/pure` loads it on the first `loadStripe()` call: when a payment sheet opens.
// The types come from the main entry as TYPE-ONLY imports, which the build
// erases; `stripe-loaded-on-demand.test.ts` holds both.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { errorMessage, refusalMessage, useT } from '../../i18n';
import {
  abandonBookingPayment,
  openBookingPayment,
  type PaymentSubject,
} from '../../lib/api/memberPayment';
import { selectedPlayerClub } from '../../lib/api/customerContext';
import { isNativeShell } from '../../lib/nativeBridge';
import { buildPaymentReturnUrl } from '../../lib/paymentReturn';
import { Dialog } from '../ui/Dialog';

// ═══════════════════════════════════════════════════════════════════
// A REAL CARD, FOR A BOOKING THAT ALREADY EXISTS (#284).
//
// This replaces `BookingPaymentModal` on the member court-booking path — a mock
// terminal with a `setTimeout` and a 5% random decline, «extracted verbatim from
// the original artifact». It is still used by the activity surfaces, which have no
// server payment path yet, and is deliberately left alone there.
//
// ── THE BOOKING COMES FIRST, AND THAT IS NOT AN ACCIDENT ────────────
//
// The old order was pay-then-book: the modal faked a receipt and the booking was
// created claiming `paymentStatus: 'paid'`. For a member that claim was already
// being discarded — `/member/bookings` derives price and status itself — so the
// theatre had no effect on the server, which is the only reason nothing was
// actually broken.
//
// Now the booking is created first and this sheet pays for the row that exists.
// That is what the server requires («the court is ALREADY held when this is
// called») and it is also what makes a failed payment harmless: a `pending`
// booking whose court is held, which the member may retry. Nothing has to be
// unwound.
//
// ── WHAT A GREEN CARD DOES NOT MEAN ────────────────────────────────
//
// `confirmPayment` succeeding means the provider accepted the card. It does NOT
// mean StarPoint has recorded a payment: the paid state arrives through the signed
// webhook, verified, and that is the only authority. So this reports «received,
// being confirmed» and never «paid» — the booking list shows what the server says.
// Claiming otherwise here would be the browser deciding that money moved, which is
// the one thing decision 8 forbids.
// ═══════════════════════════════════════════════════════════════════

export interface StripePaymentSheetProps {
  /**
   * How long the court is still held, in ms, or null when it is not known (#607).
   *
   * PASSED IN RATHER THAN FETCHED. `PayForBooking` owns the countdown — it is the
   * thing that keeps it in step with the server — and a sheet that read its own
   * would drift from the screen behind it and show a different minute.
   */
  heldForMs?: number | null;
  /** The booking to pay for. It must already exist server-side. */
  bookingId: string;
  /**
   * A court booking (the default), a standalone equipment booking (slice 2), or
   * a court booked at a venue's own door (#833 slice 3b) — which only changes
   * where the requests go.
   */
  subject?: PaymentSubject;
  /** Court/date/time, for the summary. Display only. */
  bookingSummary: string;
  /** Closes without paying. The booking stays `pending` and may be retried. */
  onCancel: () => void;
  /**
   * The card was accepted by the provider. The caller must treat this as
   * «submitted», not «paid» — see the header.
   */
  onSubmitted: () => void;
  /**
   * The server refused to open a payment because the booking is ALREADY PAID
   * (tester report, 2026-10-26). That is the server's word that the money has
   * landed, so the caller should show the booking as paid rather than leave the
   * member on an error in front of a row that still says «Ikke betalt».
   * Absent: the refusal is shown as before.
   */
  onAlreadyPaid?: () => void;
}

/**
 * STRIPE.JS FOR AN INTENT THAT NAMES VIPPS (#508).
 *
 * Vipps is in private preview, and Stripe.js reaches it through the beta flag
 * ALONE: with `betas: ['vipps_pm_beta_1']` it confirms under
 * `<its own version>; vipps_preview=v1` by itself. It must NOT be given
 * `apiVersion` — Stripe.js `endive` throws «Can not provide apiVersion to
 * Stripe()», and the first cut (#1116) did exactly that, so every Vipps payment
 * fell through to the plain initialisation below and Stripe refused the confirm
 * («Invalid payment_method_data[type]… you must explicitly specify a .preview
 * Stripe-Version»). Both halves proven against Stripe test mode in a real
 * browser, 2026-10-29: the constructor refusal, and a `betas`-only confirm that
 * hands off to Stripe's Vipps redirect.
 *
 * Used ONLY when the server opened the intent `with_redirect`: an in-page
 * intent, and every native-app payment, initialise exactly as before.
 */
export const STRIPE_JS_VIPPS_OPTIONS = {
  betas: ['vipps_pm_beta_1'],
} as const;

/**
 * Stripe.js for this intent — with the Vipps beta when the intent names Vipps,
 * and the plain initialisation if that is refused. Null when neither loads.
 */
async function loadStripeFor(
  key: string,
  stripeAccount: string,
  methodScope: 'in_page' | 'with_redirect',
): Promise<Stripe | null> {
  if (methodScope === 'with_redirect') {
    const withVipps = await loadStripe(key, {
      stripeAccount,
      betas: [...STRIPE_JS_VIPPS_OPTIONS.betas],
    }).catch(() => null);
    if (withVipps !== null) return withVipps;
    // CARD IS ON THE SAME INTENT. A Stripe.js that will not take the beta still
    // takes the card, and a sheet without Vipps beats a sheet without a form.
    // Its Vipps tab, if Stripe still draws one, is refused at confirm — which is
    // why this path must stay the exception, not where every payment lands.
  }
  return loadStripe(key, { stripeAccount }).catch(() => null);
}

/** The publishable key, from the build. Empty means no card payment here. */
function publishableKey(): string {
  return (window.__VITE_STRIPE_PUBLISHABLE_KEY__ ?? '').trim();
}

/** `m:ss`. The same reading as `PayForBooking`'s, which owns the number. */
function heldClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

type Phase =
  /** Asking the server to open a payment, then loading Stripe.js. */
  | { kind: 'opening' }
  /** The Element is mounted and the member may enter a card. */
  | { kind: 'ready'; amountMinor: number; currency: string }
  /** Confirming with the provider. */
  | { kind: 'confirming'; amountMinor: number; currency: string }
  /** The provider accepted it. Not «paid» — see the header. */
  | { kind: 'submitted' }
  /**
   * Nothing can be paid here, and the member is told why in words.
   *
   * `recoverable` is the one refusal with a way out (#607): an attempt opened on
   * terms that have since changed cannot be re-sent, but it CAN be abandoned — and
   * then the booking is payable again at the price that now applies. Before that
   * route existed the only honest message was «contact the club».
   */
  | { kind: 'blocked'; message: string; recoverable?: boolean };

export function StripePaymentSheet({
  bookingId,
  subject = 'booking',
  bookingSummary,
  heldForMs = null,
  onCancel,
  onSubmitted,
  onAlreadyPaid,
}: StripePaymentSheetProps) {
  const t = useT();
  const titleId = `stripe-sheet-title-${useId().replace(/:/g, '')}`;
  const [phase, setPhase] = useState<Phase>({ kind: 'opening' });
  // Read through a ref inside the open effect, whose dependencies are the
  // booking and the attempt only — a new callback identity must not reopen it.
  const alreadyPaidRef = useRef(onAlreadyPaid);
  alreadyPaidRef.current = onAlreadyPaid;
  /**
   * Bumped to re-run the open effect after the outstanding attempt is abandoned.
   *
   * A COUNTER RATHER THAN A FUNCTION CALL, because opening is an effect: it loads
   * Stripe.js, mounts Elements and has a cancellation path. Calling its body again
   * by hand would mount a second Element over the first.
   */
  const [attempt, setAttempt] = useState(0);
  const [cardError, setCardError] = useState<string | null>(null);
  const mountPoint = useRef<HTMLDivElement | null>(null);
  const walletPoint = useRef<HTMLDivElement | null>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  /**
   * Whether this browser actually offers a wallet (#284).
   *
   * Apple Pay in Safari, Google Pay in Chrome — and NEITHER in a browser without
   * one, where the Express Checkout Element renders nothing. The row and its
   * «eller» divider are hidden until the element says what it found, so a browser
   * with no wallet never shows an empty box with a separator under it.
   */
  const [walletReady, setWalletReady] = useState(false);
  /**
   * The CURRENT `pay`, for the wallet's one-time `confirm` handler.
   *
   * That handler is registered inside the mount effect, so it closes over the
   * render that registered it — where `phase` is still `opening` and `pay`
   * returns immediately. The card button does not have the problem because JSX
   * re-binds it every render; the wallet button would have silently done nothing.
   */
  const payRef = useRef<() => Promise<boolean>>(async () => false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const key = publishableKey();
      if (key === '') {
        // A build without the key cannot take a card. Said plainly rather than
        // failing inside Stripe.js with something a member cannot act on.
        setPhase({ kind: 'blocked', message: t('booking.pay.notConfigured') });
        return;
      }

      // ── CAN THIS PAYER COME BACK FROM VIPPS? (#508) ──────────────────
      //
      // A browser can: it leaves for Vipps and Stripe sends it back to the
      // return URL, where the cookie session is still there (#569, #750). The
      // NATIVE SHELL cannot yet — `WebShell` sends every off-site navigation to
      // the system browser, so the member would finish in Safari or Chrome, not
      // in the app (owner decision 2026-10-28; the hand-off is #1114). So the
      // app says no, and stays card only.
      const opened = await openBookingPayment(bookingId, subject, {
        redirect: !isNativeShell(),
      }).catch(() => null);
      if (cancelled) return;
      if (opened === null) {
        setPhase({ kind: 'blocked', message: t('booking.pay.unreachable') });
        return;
      }
      if (opened.state === 'refused') {
        // ALREADY PAID IS GOOD NEWS, NOT AN ERROR (tester report, 2026-10-26). The
        // row that opened this was showing a stale «Ikke betalt»; the server has
        // the payment. Hand it back so the row can say «Betalt».
        if (opened.reason === 'already_paid' && alreadyPaidRef.current) {
          alreadyPaidRef.current();
          return;
        }
        // `errorMessage` is the house translation of an API code (#310a): keys under
        // `errors.*`, an unknown code becomes the generic sentence, and the raw code
        // is logged rather than shown. My first version reimplemented this and
        // printed the code to the member — which is precisely what that helper
        // exists to prevent («how internal vocabulary leaks into the product»).
        //
        // `reason ?? error` (#1366): a booking that cannot be priced says WHY
        // (`no_booked_price`, `booked_price_invalid`) beside `unpriceable`.
        setPhase({
          kind: 'blocked',
          message: refusalMessage(t, opened.reason, opened.detail),
          // ONLY THIS ONE. «Already paid», «not your booking» and «the club cannot
          // accept payment» are not states a member can retry out of, and offering
          // a button that cannot work is worse than offering none.
          recoverable: opened.reason === 'attempt_terms_changed',
        });
        return;
      }
      if (opened.state === 'error' || opened.payment.clientSecret === null) {
        setPhase({ kind: 'blocked', message: t('booking.pay.notConfigured') });
        return;
      }

      const { clientSecret, stripeAccount, amountMinor, currency } = opened.payment;
      // DIRECT CHARGES: the intent lives on the SELLING ORGANIZATION's account, so
      // Stripe.js is initialised against it. Without `stripeAccount` the client
      // secret does not resolve and the failure reads as a server bug. With the
      // Vipps beta only when the intent names Vipps (#508).
      const stripe = await loadStripeFor(
        key,
        stripeAccount,
        opened.payment.methodScope ?? 'in_page',
      );
      if (cancelled) return;
      if (stripe === null) {
        setPhase({ kind: 'blocked', message: t('booking.pay.unreachable') });
        return;
      }

      const elements = stripe.elements({ clientSecret });
      stripeRef.current = stripe;
      elementsRef.current = elements;
      setPhase({ kind: 'ready', amountMinor, currency });
      // Mounted after the phase change, so the target div exists.
      queueMicrotask(() => {
        if (cancelled || mountPoint.current === null) return;
        elements.create('payment').mount(mountPoint.current);

        // ── THE WALLETS, ON THE SAME INTENT ─────────────────────────
        //
        // Apple Pay and Google Pay are CARD payment methods at Stripe: they hand
        // over a card rather than opening a new rail. So this needs no change to
        // the PaymentIntent — `payment_method_types: ['card']` (#622) already
        // admits them — and nothing in the #283 seam moves. The webhook is still
        // the only authority on whether money moved; a wallet only replaces the
        // typing.
        //
        // IT REQUIRES A VERIFIED DOMAIN, registered on the account the intent
        // lives on. Under direct charges that is the SELLING ORGANIZATION's
        // account, not the platform's — so a club whose domain is unregistered
        // simply sees no wallet button rather than a broken one.
        if (walletPoint.current === null) return;
        const wallets = elements.create('expressCheckout');
        wallets.on('ready', (event) => {
          if (cancelled) return;
          // `availablePaymentMethods` is undefined when the browser offers none.
          setWalletReady(event?.availablePaymentMethods !== undefined);
        });
        wallets.on('confirm', (event) => {
          // The same confirmation the card button runs — one path to one intent,
          // so «submitted» and the poll that follows it cannot differ by method.
          //
          // AND THE WALLET'S OWN SHEET HAS TO BE TOLD. Apple Pay and Google Pay
          // draw a sheet over the page and keep it there until the page resolves
          // the attempt; `paymentFailed` is how the Express Checkout Element says
          // «the merchant could not process this». Without it a declined card
          // restores OUR state and leaves THEIRS spinning, with no way out but
          // to dismiss a sheet that looks like it is still working.
          //
          // Exactly once, and only on failure: calling it after a success would
          // tell the wallet a payment we have already submitted did not happen.
          void payRef.current().then((accepted) => {
            if (!accepted) event.paymentFailed({ reason: 'fail' });
          });
        });
        wallets.mount(walletPoint.current);
      });
    };

    void run();
    return () => {
      cancelled = true;
    };
    // Once per booking, and again only when the member has deliberately abandoned
    // the attempt that was in the way. Re-running otherwise would open a second
    // Element over the first — and the server's reuse makes re-asking pointless.
  }, [bookingId, subject, t, attempt]);

  /**
   * ABANDON THE ATTEMPT IN THE WAY, THEN OPEN A NEW ONE (#607).
   *
   * Two steps, and the second must not happen unless the first SUCCEEDED. If the
   * provider will not confirm the cancellation — overwhelmingly because the card
   * went through while the member was pressing this — the attempt goes to
   * verification and opening another would risk charging twice for a payment that
   * may already have succeeded.
   */
  const abandonAndRetry = async () => {
    setPhase({ kind: 'opening' });
    const outcome = await abandonBookingPayment(bookingId, subject);
    if (outcome.state === 'ok') {
      setAttempt((n) => n + 1);
      return;
    }
    if (outcome.state === 'awaiting') {
      // NOT «cancelled», and no retry offered: the money may have moved.
      setPhase({ kind: 'blocked', message: errorMessage(t, outcome.reason) });
      return;
    }
    if (outcome.state === 'expired') {
      // THE HOLD WAS ALREADY GONE (#607). The attempt is dead and so is the court,
      // so there is nothing left to pay for here — and offering the button again
      // would open a payment for a booking that no longer exists.
      setPhase({ kind: 'blocked', message: t('errors.reservation_expired') });
      return;
    }
    if (outcome.state === 'nothing_to_do') {
      // There was nothing outstanding, so whatever refused the payment was not the
      // old attempt. Re-asking tells the member what it actually is.
      setAttempt((n) => n + 1);
      return;
    }
    setPhase({ kind: 'blocked', message: t('booking.pay.unreachable'), recoverable: true });
  };

  /**
   * Confirm the intent. Returns whether the PROVIDER accepted it.
   *
   * The card button ignores the answer — the sheet's own state already says what
   * happened. The WALLET needs it: Apple Pay and Google Pay draw their own sheet
   * over the page, and it stays there, spinning, until the page says the payment
   * could not be processed. Restoring our React state is invisible to it.
   */
  const pay = async (): Promise<boolean> => {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    // Not ready, or already confirming. The wallet is told NO rather than left
    // waiting on a confirmation this sheet is not going to start.
    if (stripe === null || elements === null || phase.kind !== 'ready') return false;
    setCardError(null);
    setPhase({ kind: 'confirming', amountMinor: phase.amountMinor, currency: phase.currency });

    const { error } = await stripe.confirmPayment({
      elements,
      // `if_required` keeps a card on the page. A method that genuinely needs a
      // redirect — Vipps, or a card's 3-D Secure — still gets one, and comes back
      // to where the member was: same page, same view, same club, with nothing
      // after a `#` for Stripe to append to (`lib/paymentReturn.ts`, #508).
      redirect: 'if_required',
      confirmParams: {
        return_url: buildPaymentReturnUrl(window.location, {
          subject,
          bookingId,
          club: selectedPlayerClub(),
        }),
      },
    });

    if (error) {
      // STRIPE'S OWN MESSAGE, which is localised and specific («your card was
      // declined», «expired card»). Replacing it with one sentence of ours would
      // tell the member less than the provider already did.
      setCardError(error.message ?? t('booking.pay.declined'));
      setPhase({ kind: 'ready', amountMinor: phase.amountMinor, currency: phase.currency });
      return false;
    }
    setPhase({ kind: 'submitted' });
    onSubmitted();
    return true;
  };
  payRef.current = pay;

  return (
    // A REAL MODAL (#1144, AUD-028/029). It was a hand-built overlay: no
    // `role="dialog"`, no name, Tab walked into the booking page behind it, and
    // focus fell to `<body>` when it closed. The shared `Dialog` gives it all of
    // that with the SAME classes on the same two elements, so nothing looks
    // different. Escape is the «Avbryt» button; the backdrop still does nothing,
    // as before — a stray tap must not abandon a card half typed in.
    <Dialog
      labelledBy={titleId}
      onClose={onCancel}
      showClose={false}
      closeOnBackdropClick={false}
      backdropClassName="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
      className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl flex flex-col overflow-hidden max-h-[92vh]"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
      testId={{ backdrop: 'stripe-sheet', panel: 'stripe-sheet-panel' }}
    >
      {/* ── A SHEET THAT FITS THE SCREEN (tester report, 2026-10-26) ──────
          The panel had no height limit and no scrolling of its own. Stripe's
          form grows tall (the optional Link fields), and a vertically centred
          panel taller than the screen is cut off at BOTH ends with neither end
          reachable: the title went above the top, the pay button below the
          bottom, and the tester could not tell which way the «menu» went.

          So: capped to the screen, a fixed HEADER (what you are paying for),
          a scrolling BODY (the payment form), and a fixed FOOTER (pay, cancel,
          the hold) — the action is always in view, and the borders mark where
          the scrolling part starts and stops. `92vh` is the floor for a browser
          without `dvh`, which drops the inline value and keeps the class. */}
      <div
        className="shrink-0 px-4 pt-4 pb-3 border-b border-black/10"
        data-testid="stripe-sheet-header"
      >
        <h3 id={titleId} className="text-[15px] font-semibold text-[#0f1611]">
          {t('booking.pay.title')}
        </h3>
        <p className="text-[12px] opacity-70 mt-1" data-testid="stripe-sheet-summary">
          {bookingSummary}
        </p>

        {(phase.kind === 'ready' || phase.kind === 'confirming') && (
          <p className="text-[13px] font-semibold mt-2" data-testid="stripe-sheet-amount">
            {t('booking.pay.total')}:{' '}
            {t('booking.pay.amount', {
              // MINOR UNITS TO A DISPLAYED FIGURE, at the last possible moment.
              // The stored and charged value stays an integer number of øre.
              amount: (phase.amountMinor / 100).toFixed(2),
              currency: phase.currency,
            })}
          </p>
        )}
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-3"
        data-testid="stripe-sheet-body"
      >
        {phase.kind === 'opening' && (
          <p className="text-[13px] mt-4" data-testid="stripe-sheet-opening">
            {t('booking.pay.opening')}
          </p>
        )}

        {phase.kind === 'blocked' && (
          <>
            <p
              role="alert"
              className="text-[13px] mt-4 text-rose-700"
              data-testid="stripe-sheet-blocked"
            >
              {phase.message}
            </p>
            {phase.recoverable === true && (
              <button
                type="button"
                onClick={() => void abandonAndRetry()}
                className="mt-3 w-full rounded-xl px-3 py-2 text-[13px] font-semibold"
                style={{ background: '#c2f02f', color: '#0f1611' }}
                data-testid="stripe-sheet-restart"
              >
                {t('booking.pay.restart')}
              </button>
            )}
          </>
        )}

        {phase.kind === 'submitted' && (
          <p className="text-[13px] mt-4" data-testid="stripe-sheet-submitted">
            {/* NOT «paid». The webhook decides that, and until it lands the
                booking is still pending — saying otherwise would be the browser
                deciding money moved. */}
            {t('booking.pay.submitted')}
          </p>
        )}

        {/* THE WALLETS, ABOVE THE CARD FORM. Hidden entirely until the element
            reports what this browser offers — a divider over an empty box is
            worse than no wallet row at all. Kept in the tree either way, because
            unmounting it is what the element's `ready` event is deciding. */}
        <div
          ref={walletPoint}
          className="mt-3"
          data-testid="stripe-sheet-wallets"
          hidden={!walletReady || (phase.kind !== 'ready' && phase.kind !== 'confirming')}
        />
        {walletReady && (phase.kind === 'ready' || phase.kind === 'confirming') && (
          <div
            className="flex items-center gap-2 mt-3 text-[11px] opacity-60"
            data-testid="stripe-sheet-wallet-divider"
          >
            <span className="h-px flex-1 bg-black/10" />
            {t('booking.pay.orCard')}
            <span className="h-px flex-1 bg-black/10" />
          </div>
        )}

        {/* The Element's own mount point. Kept in the tree across `confirming` so
            Stripe.js is never unmounted mid-confirmation. */}
        <div
          ref={mountPoint}
          className="mt-3"
          data-testid="stripe-sheet-element"
          hidden={phase.kind !== 'ready' && phase.kind !== 'confirming'}
        />

        {cardError !== null && (
          <p
            role="alert"
            className="text-[12px] mt-2 text-rose-700"
            data-testid="stripe-sheet-error"
          >
            {cardError}
          </p>
        )}
      </div>

      <div
        className="shrink-0 px-4 pt-3 pb-6 border-t border-black/10"
        data-testid="stripe-sheet-footer"
      >
        <div className="flex gap-2">
          {(phase.kind === 'ready' || phase.kind === 'confirming') && (
            <button
              type="button"
              onClick={() => void pay()}
              disabled={phase.kind === 'confirming'}
              className="flex-1 rounded-2xl font-semibold text-[14px] py-3 disabled:opacity-40"
              style={{ background: '#c2f02f', color: '#0f1611' }}
              data-testid="stripe-sheet-pay"
            >
              {phase.kind === 'confirming' ? t('booking.pay.confirming') : t('booking.pay.payNow')}
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl text-[13px] px-4 py-3 border border-black/10"
            data-testid="stripe-sheet-close"
          >
            {/* AFTER a submission this closes rather than cancels: there is
                nothing left to abandon, and offering «avbryt» would suggest the
                payment could still be called back from here. */}
            {phase.kind === 'submitted' ? t('booking.pay.close') : t('booking.pay.cancel')}
          </button>
        </div>

        {/* «The court is held for you while you pay» — WITH THE TIME when it is
            known, because a hold whose end the member cannot see is not a hold
            they can act on. Falls back to the sentence alone rather than to
            «0:00», which would be wrong in the one direction that matters. */}
        {phase.kind === 'ready' && (
          <p className="text-[11px] opacity-60 mt-3" data-testid="stripe-sheet-held">
            {heldForMs !== null && heldForMs > 0
              ? t('booking.pay.heldNoteTimed', { time: heldClock(heldForMs) })
              : t('booking.pay.heldNote')}
          </p>
        )}
      </div>
    </Dialog>
  );
}
